/**
 * Eyewear configured add-to-cart (Horizon).
 *
 * A configured lens choice is a SEPARATE priced variant that has to reach
 * the cart alongside the frame, atomically - the theme's own single-variant
 * add-to-cart has no way to do that, so this takes over that one form's
 * submit and builds the multi-item request itself.
 *
 * v3 - a prescription file no longer eats the rest of the configuration.
 *
 * The platform constraint is real and does not go away: Shopify only turns
 * a `type="file"` property into an actual stored file when the browser does
 * a genuine multipart POST straight to /cart/add, and that endpoint can only
 * ever create ONE line item per request - it has no multi-item form. The
 * JSON /cart/add.js endpoint (used for the no-file path below) accepts an
 * items[] array and can add several lines atomically, but it cannot carry a
 * File object at all; Shopify silently drops it.
 *
 * v2 treated those as mutually exclusive and picked whichever the submit
 * needed - correct for what it did, but it meant a file always meant "frame
 * only, nothing else survives", which is the bug just found: pick a paid
 * lens, attach a prescription, submit - the addon line and even the OTHER
 * selected choices (Vision Type, Lens Type as text) vanished, only the file
 * made it through.
 *
 * The fix is two REAL native submissions instead of one:
 *
 *   1. Any priced add-on choices go first, as their own native POST(s) to
 *      /cart/add, each targeted at a hidden <iframe> instead of the visible
 *      page - the standard, decades-old way to submit a form without
 *      navigating away from the page doing the submitting. This is still a
 *      genuine multipart POST, so nothing about it is fighting the platform;
 *      it is just not the one carrying the file.
 *   2. Once those have landed, hidden fields for every currently-selected
 *      group's property (the same values the JSON path already sends) are
 *      written directly onto the REAL visible form, and THAT form is then
 *      submitted for real - unprevented, so the browser performs its own
 *      native multipart POST with the file attached, landing on /cart same
 *      as before.
 *
 * Both submissions add to the SAME cart (native /cart/add always appends,
 * never replaces), so by the time the browser lands on /cart, every line -
 * frame, addon, file, and the frame's own text properties - is present.
 *
 * The no-file path is unchanged from v2: one JSON POST to /cart/add.js with
 * every line in a single atomic request, then a redirect to /cart.
 */
(function () {
  'use strict';

  function generateConfigurationId() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }
    return 'cfg-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
  }

  function getVisibleGroups(eyewearProduct) {
    return Array.from(eyewearProduct.querySelectorAll('[data-option-group]')).filter((group) => !group.hidden);
  }

  function collectSelection(eyewearProduct) {
    const properties = {};
    const addOns = [];
    let missingRequired = null;

    getVisibleGroups(eyewearProduct).forEach((group) => {
      const checked = group.querySelector('[data-choice-input]:checked');
      const isRequired = group.getAttribute('aria-required') === 'true';

      if (!checked) {
        if (isRequired && !missingRequired) missingRequired = group;
        return;
      }

      const groupLabel = group.dataset.groupLabel;
      const choiceLabel = checked.dataset.label;
      if (groupLabel && choiceLabel) properties[groupLabel] = choiceLabel;

      const priceCents = parseInt(checked.dataset.priceCents || '0', 10);
      const addOnVariantId = checked.dataset.addOnVariantId;
      if (addOnVariantId && priceCents > 0) {
        addOns.push({ id: parseInt(addOnVariantId, 10) });
      }
    });

    return { properties, addOns, missingRequired };
  }

  function getActivePrescriptionInput(root) {
    const upload = root.querySelector('eyewear-prescription-upload');
    if (!upload || upload.hidden) return null;
    const input = upload.querySelector('.eyewear-prescription__input');
    return input && input.files && input.files.length ? input : null;
  }

  function setLoading(form, isLoading) {
    const submitButton = form.querySelector('[type="submit"], button[name="add"]');
    if (!submitButton) return;
    if (isLoading) submitButton.setAttribute('aria-disabled', 'true');
    else submitButton.removeAttribute('aria-disabled');
  }

  function findConfiguratorRoot(formId) {
    return document.querySelector('.eyewear-forms[data-product-form-id="' + formId + '"]');
  }

  function disableHorizonHandler(form) {
    const host = form.closest('product-form-component');
    if (host) host.removeAttribute('on:submit');
  }

  // ---------- JSON path (no file attached) ----------

  function submitConfiguredCartJson(form, eyewearProduct, properties, addOns) {
    const formData = new FormData(form);
    const frameVariantId = parseInt(formData.get('id'), 10);
    const quantity = parseInt(formData.get('quantity'), 10) || 1;
    if (!frameVariantId) return;

    const items = [];

    if (addOns.length > 0) {
      const configurationId = generateConfigurationId();
      const optionsAmountEl = eyewearProduct.querySelector('[data-options-amount]');
      const optionsAmountText = optionsAmountEl ? optionsAmountEl.textContent.trim() : '';

      // Add-ons FIRST, frame LAST: Shopify's /cart/add.js reliably displays
      // a multi-item add in the REVERSE of the order items were sent
      // (confirmed empirically) - the frame needs to read as the top line.
      addOns.forEach((addOn) => {
        items.push({
          id: addOn.id,
          quantity,
          properties: { '_Configuration ID': configurationId, '_Linked Frame Variant': String(frameVariantId) },
        });
      });
      items.push({
        id: frameVariantId,
        quantity,
        properties: Object.assign({}, properties, { 'Options Amount': optionsAmountText, '_Configuration ID': configurationId }),
      });
    } else {
      items.push({ id: frameVariantId, quantity, properties: Object.keys(properties).length ? properties : undefined });
    }

    setLoading(form, true);

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items }),
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          window.alert(response.description || response.message || 'Could not add to cart.');
          setLoading(form, false);
          return;
        }
        window.location = '/cart';
      })
      .catch(() => {
        window.alert('Something went wrong, please try again.');
        setLoading(form, false);
      });
  }

  // ---------- Native path (file attached) ----------

  let hiddenFrameCounter = 0;

  // Submits ONE add-on variant as its own real multipart POST, targeted at a
  // throwaway hidden iframe so it never navigates the visible page. Resolves
  // once the iframe has finished loading the response (i.e. Shopify has
  // processed the add), so the caller can safely queue the next one.
  function submitAddOnNatively(addOn, quantity, configurationId, frameVariantId) {
    return new Promise((resolve) => {
      hiddenFrameCounter += 1;
      const frameName = 'eyewear-addon-frame-' + hiddenFrameCounter;

      const iframe = document.createElement('iframe');
      iframe.name = frameName;
      iframe.style.display = 'none';
      document.body.appendChild(iframe);

      const cleanup = () => {
        iframe.removeEventListener('load', onLoad);
        // Give the browser a tick before removing - some browsers abort an
        // in-flight submission if its target iframe is removed too eagerly.
        setTimeout(() => iframe.remove(), 0);
      };
      const onLoad = () => {
        cleanup();
        resolve();
      };
      // Belt and braces: if the load event never fires for any reason, don't
      // hang the whole submit forever.
      const timeout = setTimeout(() => {
        cleanup();
        resolve();
      }, 8000);
      iframe.addEventListener('load', () => {
        clearTimeout(timeout);
        onLoad();
      });

      const form = document.createElement('form');
      form.method = 'post';
      form.action = '/cart/add';
      form.enctype = 'multipart/form-data';
      form.target = frameName;
      form.style.display = 'none';

      const fields = {
        id: addOn.id,
        quantity: quantity,
        return_to: '/cart',
        'properties[_Configuration ID]': configurationId,
        'properties[_Linked Frame Variant]': String(frameVariantId),
      };
      Object.keys(fields).forEach((name) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = fields[name];
        form.appendChild(input);
      });

      document.body.appendChild(form);
      form.submit();
      // The form element itself can be removed right away - the browser has
      // already captured what it needs to submit; only the iframe (the
      // submission's target) has to survive until it loads.
      form.remove();
    });
  }

  function injectHiddenProperties(form, properties) {
    // Clear any previous run's injected fields first, in case this same
    // form submits more than once in a page's lifetime.
    form.querySelectorAll('[data-eyewear-injected-property]').forEach((el) => el.remove());
    Object.keys(properties).forEach((key) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'properties[' + key + ']';
      input.value = properties[key];
      input.setAttribute('data-eyewear-injected-property', '');
      form.appendChild(input);
    });

    // Dawn's product form and Horizon's both leave return_to unset on this
    // form, which means Shopify's native /cart/add has no instruction on
    // where to send the browser afterward and can fall back to returning
    // the raw item JSON instead of landing on the cart page. Every other
    // add-to-cart on the site never hits this path (Horizon's own JS
    // intercepts it before it becomes a real navigation), so nothing
    // upstream ever needed to set this - this file-carrying submission is
    // the one real native browser navigation in the whole flow, and it is
    // the only one that needs it.
    if (!form.querySelector('input[name="return_to"]')) {
      const returnTo = document.createElement('input');
      returnTo.type = 'hidden';
      returnTo.name = 'return_to';
      returnTo.value = '/cart';
      returnTo.setAttribute('data-eyewear-injected-property', '');
      form.appendChild(returnTo);
    }
  }

  function submitConfiguredCartNative(form, eyewearProduct, properties, addOns) {
    const formData = new FormData(form);
    const frameVariantId = parseInt(formData.get('id'), 10);
    const quantity = parseInt(formData.get('quantity'), 10) || 1;
    if (!frameVariantId) return;

    setLoading(form, true);

    const configurationId = addOns.length > 0 ? generateConfigurationId() : null;
    const frameProperties = Object.assign({}, properties);
    if (configurationId) {
      const optionsAmountEl = eyewearProduct.querySelector('[data-options-amount]');
      frameProperties['Options Amount'] = optionsAmountEl ? optionsAmountEl.textContent.trim() : '';
      frameProperties['_Configuration ID'] = configurationId;
    }

    // Add-ons first, one real native POST each, awaited in sequence so they
    // are guaranteed to land before the page navigates away with the final
    // (file-carrying) submission.
    let chain = Promise.resolve();
    addOns.forEach((addOn) => {
      chain = chain.then(() => submitAddOnNatively(addOn, quantity, configurationId, frameVariantId));
    });

    chain.then(() => {
      injectHiddenProperties(form, frameProperties);
      // Native submit, NOT requestSubmit() - HTMLFormElement.prototype.submit
      // does not dispatch a 'submit' event at all, so this cannot re-trigger
      // this same delegated listener (or Horizon's own) a second time.
      HTMLFormElement.prototype.submit.call(form);
    });
  }

  // ---------- wiring ----------

  document.addEventListener(
    'submit',
    function (event) {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;

      const formId = form.getAttribute('id');
      if (!formId) return;

      const root = findConfiguratorRoot(formId);
      if (!root) return; // not an eyewear-configured product - leave Horizon's own flow alone entirely

      disableHorizonHandler(form);

      const eyewearProduct = root.querySelector('eyewear-product');
      if (!eyewearProduct) return;

      const { properties, addOns, missingRequired } = collectSelection(eyewearProduct);

      if (missingRequired) {
        event.preventDefault();
        const label = missingRequired.dataset.groupLabel || 'a required option';
        window.alert('Please select ' + label + ' before adding to cart.');
        missingRequired.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }

      const fileInput = getActivePrescriptionInput(root);
      if (fileInput) {
        // Still take over entirely - the previous version stepped aside
        // completely here and let the file-only native submit through
        // untouched, which is exactly what lost the rest of the
        // configuration. This one preventDefaults the browser's own
        // click-triggered submit and drives BOTH the add-on line(s) and the
        // final file-carrying submission itself, in order.
        event.preventDefault();
        submitConfiguredCartNative(form, eyewearProduct, properties, addOns);
        return;
      }

      event.preventDefault();
      submitConfiguredCartJson(form, eyewearProduct, properties, addOns);
    },
    true
  );

  window.__eyewearInitCart = function (root, formId) {
    if (!root || !formId) return;
    root.setAttribute('data-product-form-id', formId);
    const form = document.getElementById(formId);
    if (form) disableHorizonHandler(form);
  };
})();
